// Sopwith 3D - the three.js world renderer.
//
// Owns the WebGL renderer, scene, lights, terrain, camera, object views,
// particles and post-processing. The simulation is the single source of
// truth: call `commit()` after every tick and `render(alpha, dt)` per frame.
// `bind()` switches to a new Game (new round or a fresh start).

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { MAX_Y } from '../sim/constants';
import type { Game } from '../sim/game';
import { FollowCamera } from './camera';
import { Particles } from './effects';
import { Sky } from './sky';
import { Terrain } from './terrain';
import { ViewSet } from './views';

export class WorldRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: FollowCamera;
  terrain!: Terrain;
  readonly views: ViewSet;
  readonly particles = new Particles();
  private readonly sky = new Sky();
  private readonly sun: THREE.DirectionalLight;
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private game: Game;
  private time = 0;
  private attractX = 0;
  private attractDir = 1;
  private readonly container: HTMLElement;

  constructor(container: HTMLElement, game: Game) {
    this.container = container;
    this.game = game;
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.scene.fog = new THREE.Fog(0xd8ecf7, 700, 1800);
    this.scene.add(this.sky.mesh);

    const hemi = new THREE.HemisphereLight(0xdcefff, 0x2f4a22, 0.8);
    this.sun = new THREE.DirectionalLight(0xfff4de, 1.9);
    this.sun.position.set(-200, 400, 300);
    this.scene.add(hemi, this.sun, this.particles.points);

    this.camera = new FollowCamera(container.clientWidth / container.clientHeight);
    this.views = new ViewSet(this.scene, this.particles);

    const size = new THREE.Vector2(container.clientWidth, container.clientHeight);
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera.camera));
    this.bloom = new UnrealBloomPass(size, 0.55, 0.5, 0.9);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    this.bind(game);

    new ResizeObserver(() => this.resize()).observe(container);
  }

  private resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.camera.resize(w / h);
    this.terrain.resize(w, h);
  }

  /** Attach a (new) game: rebuild terrain and views, recentre the camera. */
  bind(game: Game): void {
    this.game = game;
    if (this.terrain) {
      this.scene.remove(this.terrain.group);
    }
    this.terrain = new Terrain(game.ground);
    this.terrain.resize(this.container.clientWidth, this.container.clientHeight);
    this.scene.add(this.terrain.group);
    this.camera.worldWidth = game.level.maxX;
    this.views.clear();
    this.commit();
    this.snapCamera();
  }

  private playerCentreX(): number {
    return this.game.consoleplayer.x + 8;
  }

  /** Call after each simulation tick. */
  commit(): void {
    this.views.commit(this.game.world.objtop);
    this.terrain.update(this.game.ground);
  }

  /** Call once per animation frame with the fraction of the current tick elapsed. */
  render(alpha: number, dt: number): void {
    this.time += dt;
    this.views.present(alpha, this.time);
    this.particles.update(dt);

    const p = this.game.consoleplayer;
    const pv = this.views.get(p);
    const px = pv ? pv.tmp.x + 8 : this.playerCentreX();
    this.camera.update(px, p.dx, dt);
    this.finishFrame(dt);
  }

  /** Title-screen flyover: pan slowly across the world with the sim paused. */
  renderAttract(dt: number): void {
    this.time += dt;
    this.views.present(0, this.time);
    this.particles.update(dt);
    const maxX = this.game.level.maxX;
    this.attractX += this.attractDir * 45 * dt;
    if (this.attractX > maxX - 200) this.attractDir = -1;
    if (this.attractX < 200) this.attractDir = 1;
    this.camera.update(this.attractX, this.attractDir, dt);
    this.finishFrame(dt);
  }

  private finishFrame(_dt: number): void {
    const cx = this.camera.camera.position.x;
    this.sky.follow(cx);
    this.sun.position.x = cx - 200;
    this.sun.target.position.set(cx, MAX_Y / 2, 0);
    this.sun.target.updateMatrixWorld();
    this.composer.render();
  }

  /** Recentre the camera without smoothing (level restart). */
  snapCamera(): void {
    this.attractX = this.playerCentreX();
    this.camera.snap(this.playerCentreX());
  }
}
